import React, { useEffect, useState, useCallback } from "react";
import { fetchPersons, updatePerson } from "../../../services/aro/personService";
import { fetchEvents }    from "../../../services/aro/eventService";
import { PersonListItem, AROEvent } from "../../../types/aro";
import LoadingSpinner    from "../shared/LoadingSpinner/LoadingSpinner";
import EmptyState        from "../shared/EmptyState/EmptyState";
import PageHeader        from "../../shared/PageHeader/PageHeader";
import { formatDateLong } from "../../../utils/formatters";
import { useAuth } from "../../../context/useAuth";
import { canEditLinkedin } from "../../../utils/quarantineAccess";
import "./PeoplePage.css";

const LIMIT = 50;

interface Props {
  onSelect: (id: number) => void;
}

interface CommittedFilters {
  name:           string;
  email:          string;
  eventId:        string;
  highestLevel:   string;
  noLinkedin:     boolean;
}

const DEFAULT_COMMITTED: CommittedFilters = {
  name:           "",
  email:          "",
  eventId:        "all",
  highestLevel:   "all",
  noLinkedin:     false,
};

export default function PeoplePage({ onSelect }: Props) {
  const { email: userEmail } = useAuth();
  const canEdit              = canEditLinkedin(userEmail);

  const [persons,  setPersons]  = useState<PersonListItem[]>([]);
  const [total,    setTotal]    = useState(0);
  const [offset,   setOffset]   = useState(0);
  const [events,   setEvents]   = useState<AROEvent[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState<string | null>(null);

  // Input state
  const [name,           setName]           = useState("");
  const [email,          setEmail]          = useState("");
  const [eventId,        setEventId]        = useState("all");
  const [highestLevel,   setHighestLevel]   = useState("all");
  const [noLinkedin,     setNoLinkedin]     = useState(false);

  // Committed state
  const [committed, setCommitted] = useState<CommittedFilters>(DEFAULT_COMMITTED);

  // Inline LinkedIn edit state
  const [editingId,     setEditingId]     = useState<number | null>(null);
  const [linkedinDraft, setLinkedinDraft] = useState("");
  const [editSaving,    setEditSaving]    = useState(false);
  const [editError,     setEditError]     = useState<string | null>(null);

  useEffect(() => {
    fetchEvents()
      .then((r) => setEvents(r.events))
      .catch(() => {});
  }, []);

  const load = useCallback((off: number) => {
    setLoading(true);
    setError(null);
    fetchPersons({
      name:             committed.name || undefined,
      email:            committed.email || undefined,
      event_id:         committed.eventId !== "all" ? committed.eventId : undefined,
      highest_level:    committed.highestLevel,
      no_linkedin:      committed.noLinkedin || undefined,
      limit:            LIMIT,
      offset:           off,
    })
      .then((r) => { setPersons(r.persons); setTotal(r.total); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [committed]);

  useEffect(() => { setOffset(0); load(0); }, [load]);

  const handleSearch = () => {
    setEditingId(null);
    setCommitted({ name, email, eventId, highestLevel, noLinkedin });
  };

  const clearFilters = () => {
    setName(""); setEmail(""); setEventId("all");
    setHighestLevel("all"); setNoLinkedin(false);
    setCommitted(DEFAULT_COMMITTED);
  };

  const openLinkedinEdit = (p: PersonListItem) => {
    setEditingId(p.id);
    setLinkedinDraft(p.linkedin_url ?? "");
    setEditError(null);
  };

  const cancelLinkedinEdit = () => {
    setEditingId(null);
    setLinkedinDraft("");
    setEditError(null);
  };

  const saveLinkedin = async (p: PersonListItem) => {
    if (!linkedinDraft.trim()) return;
    setEditSaving(true);
    setEditError(null);
    try {
      // Show what the server saved (cleaned URL) and the tracking it reactivated
      const saved = await updatePerson(p.id, { linkedin_url: linkedinDraft.trim() }, userEmail);
      setPersons((prev) =>
        prev.map((r) => r.id === p.id ? { ...r, linkedin_url: saved.linkedin_url, tracking: saved.tracking } : r)
      );
      setEditingId(null);
    } catch (e: any) {
      setEditError(e.message ?? "Failed to save.");
    } finally {
      setEditSaving(false);
    }
  };

  const from = total === 0 ? 0 : offset + 1;
  const to   = Math.min(offset + LIMIT, total);

  return (
    <div className="aro-page">
      <PageHeader title="People" subtitle={`${total} people tracked`} />

      <div className="aro-filter-bar">
        <input
          className="aro-input"
          placeholder="Name…"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
        />
        <input
          className="aro-input"
          placeholder="Email…"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
        />
        <select className="aro-select" value={eventId} onChange={(e) => setEventId(e.target.value)}>
          <option value="all">All Events</option>
          {events.map((ev) => (
            <option key={ev.id} value={ev.id}>{ev.event_name}</option>
          ))}
        </select>
        <select className="aro-select" value={highestLevel} onChange={(e) => setHighestLevel(e.target.value)}>
          <option value="all">All Levels</option>
          <option value="1">Highest: Level 1</option>
          <option value="2">Highest: Level 2</option>
          <option value="3">Highest: Level 3</option>
        </select>
        <label className="aro-toggle">
          <input
            type="checkbox"
            checked={noLinkedin}
            onChange={(e) => setNoLinkedin(e.target.checked)}
          />
          <span>Missing LinkedIn</span>
        </label>
        <button className="aro-btn aro-btn--primary" onClick={handleSearch}>Search</button>
        <button className="aro-btn aro-btn--ghost" onClick={clearFilters}>Clear</button>
      </div>

      {error && <div className="aro-error-banner">{error}</div>}

      <div className="aro-card aro-card--no-pad">
        {loading ? (
          <LoadingSpinner />
        ) : persons.length === 0 ? (
          <EmptyState icon="👤" message="No people found." />
        ) : (
          <div className="aro-table-scroll">
            <table className="aro-table">
              <thead>
                <tr>
                  <th>Full Name</th>
                  <th>Email</th>
                  <th>LinkedIn</th>
                  <th>Ingested Date</th>
                </tr>
              </thead>
              <tbody>
                {persons.map((p, i) => (
                  <React.Fragment key={p.id}>
                    <tr
                      className={[
                        i % 2 === 1 ? "aro-table__row--odd" : "",
                        "aro-table__row--clickable",
                      ].join(" ")}
                      onClick={() => editingId !== p.id && onSelect(p.id)}
                    >
                      <td className="aro-table__cell--primary">{p.full_name || p.email}</td>
                      <td>{p.email}</td>
                      <td onClick={(e) => e.stopPropagation()}>
                        {p.linkedin_url ? (
                          <a
                            className="aro-external-link"
                            href={p.linkedin_url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            ↗ LinkedIn
                          </a>
                        ) : (
                          <span className="people-missing-linkedin">
                            <span className="aro-missing-linkedin">Missing</span>
                            {canEdit && (
                              <button
                                className="aro-btn aro-btn--ghost aro-btn--sm"
                                onClick={() => openLinkedinEdit(p)}
                              >
                                + Add
                              </button>
                            )}
                          </span>
                        )}
                      </td>
                      <td>{formatDateLong(p.created_at)}</td>
                    </tr>

                    {/* Inline LinkedIn edit row */}
                    {editingId === p.id && (
                      <tr>
                        <td colSpan={4} className="people-inline-edit-row">
                          <div className="people-inline-edit">
                            <span className="people-inline-edit__label">
                              Add LinkedIn URL for <strong>{p.full_name || p.email}</strong>
                            </span>
                            <input
                              className="aro-input"
                              style={{ flex: 1, minWidth: 280 }}
                              value={linkedinDraft}
                              onChange={(e) => setLinkedinDraft(e.target.value)}
                              placeholder="https://linkedin.com/in/..."
                              autoFocus
                              onKeyDown={(e) => e.key === "Enter" && saveLinkedin(p)}
                            />
                            {editError && (
                              <span className="people-inline-edit__error">{editError}</span>
                            )}
                            <button
                              className="aro-btn aro-btn--primary aro-btn--sm"
                              disabled={editSaving || !linkedinDraft.trim()}
                              onClick={() => saveLinkedin(p)}
                            >
                              {editSaving ? "Saving…" : "Save"}
                            </button>
                            <button
                              className="aro-btn aro-btn--ghost aro-btn--sm"
                              onClick={cancelLinkedinEdit}
                            >
                              Cancel
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {total > LIMIT && (
        <div className="aro-pagination">
          <span className="aro-pagination__info">
            Showing {from}–{to} of {total}
          </span>
          <button
            className="aro-btn aro-btn--ghost"
            disabled={offset === 0}
            onClick={() => { const o = Math.max(0, offset - LIMIT); setOffset(o); load(o); }}
          >
            ← Previous
          </button>
          <button
            className="aro-btn aro-btn--ghost"
            disabled={offset + LIMIT >= total}
            onClick={() => { const o = offset + LIMIT; setOffset(o); load(o); }}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
